/// <reference path="../../app.definition.ts" />

/*
* Module: SVG
* author: Ian Tripp
*
* Contains helper methods to create SVG objects
*/
import angular from 'angular';
import * as HTMLUtil from "@common/utility/html";

export const CLOCKWISE: boolean = true;
export const COUNTER_CLOCKWISE: boolean = false;
export const ORIGIN: HTMLUtil.Offset = new HTMLUtil.Offset(0, 0);

export const Shapes: any = {
    CIRCLE: 'circle',
    RECT: 'rect'
};

// Represents a shape within an SVG object
export class Shape {
    shape: string; //type of shape (circle or rect)
    size: HTMLUtil.ElementSize; //size of shape
    offset: HTMLUtil.Offset //offset of shape within SVG
    direction: boolean // Direction path should be drawn (fill in shape or cutout shape from backgrounf)

    constructor(shape: string, size: HTMLUtil.ElementSize, offset: HTMLUtil.Offset, dir?: boolean) {
        this.shape = shape;
        this.size = size;
        this.offset = offset;
        this.direction = !!dir;
    }

    // Call the appropriate SVG path creation function based on shape type
    getPath = (): string => {
        switch (this.shape) {
            case Shapes.RECT: return createRectPath(this.size, this.offset, this.direction);
            case Shapes.CIRCLE: return createCirclePath(this.size, this.offset, this.direction);
        }
    }
}
export class Element {
    protected selector: string;
    protected $el: JQuery<HTMLElement>;
    protected type: string;
    protected initialized: boolean;

    constructor(selector: string, definition?: string) {
        this.initialized = false;
        this.selector = selector;
        if (!!selector && !!definition) {
            return;
        }


        if (!!definition) {
            this.$el = angular.element(definition);
            this.$el.attr({ id: selector });
        } else {
            this.$el = angular.element(selector);

            if (!this.$el[0]) {
                this.$el = null;
            } else {
                this.initialized = true;
            }
        }
    }

    getElement = (): JQuery<HTMLElement> => {
        return this.$el;
    }

    getHTML = (): string => {
        throw "Unsupported Operation";
    }

    setSize = (size: HTMLUtil.ElementSize): void => {
        this.$el.css({ width: size.outerWidth, height: size.outerHeight });
    }

    setOffset = (offset: HTMLUtil.Offset): void => {
        this.$el.offset({ top: offset.top, left: offset.left });
    }

    setPosition = (offset: HTMLUtil.Offset): void => {
        this.$el.position();
    }
}

export class SVGElement extends Element {
    private container: JQuery<HTMLElement>;
    private $g: JQuery<HTMLElement>;
    private baseShape: Shape;
    private pathStr: string;
    private $path: JQuery<HTMLElement>;


    constructor(selector: string, baseShape?: Shape) {
        super(selector);
        this.type = 'svg';
        this.container = angular.element(`<div class="svg-container"></div>`);
        !!baseShape && (this.baseShape = baseShape);
        if (!this.initialized) {
            //initialize to svg with size 0,0
            this.init();
        }

    }

    init = (): void => {
        this.$el = angular.element(svgTag(0, 0));
        this.$el.append(this.$g = angular.element(gTag({ 'pointer-events': 'visibleFill' })))
            .attr('id', this.selector);
        this.$g.append(this.$path = angular.element(`<path id="svg-base-path" fill="black" opacity="0.75"></path>`));

        this.setBasePath();
        this.container.append(this.$el);
        this.initialized = true;
    }

    setBasePath = () => {
        if (!!this.baseShape) {
            this.$path.attr('d', this.pathStr = this.baseShape.getPath());
        }
    }

    resetPath = () => {
        this.$el.remove();
        this.init();
    }

    render = () => {
        this.container.html(this.container.html());
        this.$el = this.container.find('svg');
        this.$g = this.$el.find('g');
        this.$path = this.$g.find('path');
    }

    getGroup = (): JQuery<HTMLElement> => this.$g;

    getContainer = (): JQuery<HTMLElement> => this.container;

    addShape = (shape: Shape): void => {
        // console.log("Adding", shape.getPath(), "to", this.pathStr);
        this.pathStr += ' ' + shape.getPath()
        this.$path.attr('d', this.pathStr);
    }

    update = (): void => {
        // console.log("Updated $g:", this.$g);
        // this.container.html(this.container.html());
    }

    setSize = (size: HTMLUtil.ElementSize): void => {
        this.$el.attr({ width: size.outerWidth, height: size.outerHeight });
        if (!!this.baseShape) {
            this.baseShape.size = size;
        }
    }
}

// Represents a single drawn path in an SVG object.  Can contain multiple shapes.
export class Path {
    id: string;
    shapes: Shape[]; // The shapes that create the path
    fill: string; // color of the fill
    opacity: string; // opacity of the fill

    constructor(shapes?: Shape[], fill?: string, opacity?: string, id?: string) {
        this.shapes = shapes || [];
        !!fill && (this.fill = fill);
        !!opacity && (this.opacity = opacity);
        !!id && (this.id = id);
    }

    addShape = (shape: Shape): void => {
        this.shapes.push(shape);
    }

    // Construct an SVG path node based on the shapes that it contains
    toString = (): string => {
        let path = `<path d="` + this.shapes.map(s => s.getPath()).join(' ') + `"`
        !!this.fill && (path += ' fill="' + this.fill + '"');
        !!this.opacity && (path += ' opacity="' + this.opacity + '"');
        !!this.id && (path += ' id="' + this.id + '"');

        path += `></path>`
        return path;
    }
}

/*
 * Convert corner coordinates and size dimensions into center coordinates
 * and radius dimensions to create a circle as a SVG path. Each circle is
 * constructed from two 180 degree arcs
 */
export let createCirclePath = (size: HTMLUtil.ElementSize, offset: HTMLUtil.Offset, dir?: boolean): string => {
    let center = new HTMLUtil.Offset(
        offset.left + (size.outerWidth / 2),
        offset.top + (size.outerHeight / 2)
    );

    let cr = Math.max(size.outerWidth, size.outerHeight) / 2;
    let sweep = !!dir ? 1 : 0;
    let arc = `M` + center.left + ` ` + center.top + ` `;
    arc += `m -` + cr + `, 0 `;
    arc += `a ` + cr + `, ` + cr + ` 0 1, ` + sweep + ` ` + (cr * 2) + `, 0 `;
    arc += `a ` + cr + `, ` + cr + ` 0 1, ` + sweep + ` -` + (cr * 2) + `, 0`;
    return arc
}

/*
 * Using coordinates and size dimensions, create a rectangle as a SVG path
 */
export let createRectPath = (size: HTMLUtil.ElementSize, offset: HTMLUtil.Offset, dir?: boolean): string => {
    let rect = `M ` + offset.left + ` ` + offset.top + ` `;
    let w = size.outerWidth, h = size.outerHeight;
    return !!dir ? rect + `h ` + w + ` v ` + h + ` h -` + w + ` Z` :
        rect + `v ` + h + ` h ` + w + ` v -` + h + ` Z`;
}

// Create an SVG element node based on a list of paths and a parameter object
export let createSVGFromPaths = (size: HTMLUtil.ElementSize, paths: Path[], params?: any): string => {
    let svgFrame = `<svg xmlns="http://www.w3.org/2000/svg" width="` + size.innerWidth + `" height="` + size.innerHeight + `"><g`;
    let paramTmp = params || {};
    for (let key in paramTmp) {
        let val = paramTmp[key];
        svgFrame += ` $key="$val"`.replace('$key', key).replace('$val', val);
    }

    svgFrame += `>`

    for (let path of paths || []) {
        svgFrame += path.toString().replace('--', '');

    }

    svgFrame += `</g></svg>`;

    return svgFrame;
}

export let svgTag = (width?: number, height?: number): string => {
    return `<svg xmlns="http://www.w3.org/2000/svg"`
        + (!!width ? ` width="` + width + `"` : ``)
        + (!!height ? ` height="` + height + `"` : ``)
        + `>`;
}

export let gTag = (params?): string => {
    let paramTmp = params || {};
    let g = `<g`
    for (let key in paramTmp) {
        let val = paramTmp[key];
        g += ` $key="$val"`.replace('$key', key).replace('$val', val);
    }
    return g + `></g>`;
}
